'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { PerformanceLabPhoneIcon } from 'assets/icons/PerformanceLabPhoneIcon';
import { getPerformanceLabLenis } from 'utils/performanceLabLenisRegistry';

import {
  PERFORMANCE_LAB_PHONE_E164,
  PERFORMANCE_LAB_PHONE_HREF,
} from './performanceLabContactLinks';
import {
  useIsIndiaRegion,
  usePerformanceLabCartItemCount,
} from './hooks';

import styles from './FloatingBookVisitCTA.module.css';

const MENU_GAP_PX = 12;
const FAB_EDGE_INSET_PX = 32;
const PL_CART_BAR_OFFSET_PX = 100;

export const FloatingBookVisitCTA = () => {
  const t = useTranslations('performance-lab');
  const isIndia = useIsIndiaRegion();
  const plCartItemCount = usePerformanceLabCartItemCount();
  const cartBarOffsetPx =
    isIndia && plCartItemCount > 0 ? PL_CART_BAR_OFFSET_PX : 0;
  const [copied, setCopied] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);

  /**
   * Keep the FAB clear of the hero's bottom marquee. Driven entirely through a
   * CSS variable written via the DOM (no React re-render or layout read on
   * scroll): marquee geometry is cached on mount/resize, the scroll handler only
   * reads scrollY. While the marquee would collide the FAB rides just above it
   * (no transition, so it tracks the scroll 1:1); once there's room below it the
   * FAB drops to its resting position with a one-off transition.
   */
  useEffect(() => {
    const wrap = wrapperRef.current;
    if (!wrap) return undefined;

    const normalBottom = FAB_EDGE_INSET_PX + cartBarOffsetPx;
    const cache = { topDoc: 0, height: 0, fabH: 0, vh: 0, hasMarquee: false };
    let mode: 'track' | 'rest' = 'rest';
    let raf = 0;

    const apply = (bottom: number, nextMode: 'track' | 'rest') => {
      if (nextMode !== mode) {
        wrap.style.transition =
          nextMode === 'track' ? 'none' : 'bottom 0.3s ease-out';
        mode = nextMode;
      }
      wrap.style.setProperty('--pl-fab-bottom', `${bottom}px`);
    };

    const update = () => {
      if (!cache.hasMarquee) {
        apply(normalBottom, 'rest');
        return;
      }
      const y = window.scrollY;
      const bottomVp = cache.topDoc + cache.height - y;
      const topVp = cache.topDoc - y;
      const needed = cache.fabH + FAB_EDGE_INSET_PX;
      if (bottomVp <= 0 || cache.vh - bottomVp >= needed) {
        apply(normalBottom, 'rest');
      } else {
        apply(Math.max(normalBottom, cache.vh - topVp + MENU_GAP_PX), 'track');
      }
    };

    const remeasure = () => {
      const marquee = document.querySelector('[data-pl-marquee]');
      cache.vh = window.innerHeight;
      cache.fabH = wrap.offsetHeight;
      if (marquee) {
        const rect = marquee.getBoundingClientRect();
        cache.topDoc = rect.top + window.scrollY;
        cache.height = rect.height;
        cache.hasMarquee = true;
      } else {
        cache.hasMarquee = false;
      }
      update();
    };

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };

    remeasure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', remeasure);
    getPerformanceLabLenis()?.on?.('scroll', onScroll);
    const t1 = window.setTimeout(remeasure, 300);
    const t2 = window.setTimeout(remeasure, 1200);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', remeasure);
      getPerformanceLabLenis()?.off?.('scroll', onScroll);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [cartBarOffsetPx]);

  const copyPhoneNumber = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(PERFORMANCE_LAB_PHONE_E164);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, []);

  const phoneDisplayText = t('getDirections.phoneText');

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <span className={styles.copiedLive} aria-live="polite" aria-atomic="true">
        {copied ? t('floatingCall.copied') : ''}
      </span>
      <button
        type="button"
        className={styles.phoneNumberButton}
        aria-label={t('floatingCall.copyPhoneAria')}
        data-buttontype="performance_lab_copy_phone"
        onClick={copyPhoneNumber}
      >
        {copied ? t('floatingCall.copied') : phoneDisplayText}
      </button>
      <a
        className={styles.callButton}
        href={PERFORMANCE_LAB_PHONE_HREF}
        aria-label={t('floatingCall.callNowAria')}
        data-buttontype="performance_lab_call_now"
      >
        <PerformanceLabPhoneIcon />
      </a>
    </div>
  );
};
